import React, { createContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '@/shared/api/authApi'
import { useAuthStore } from '@/shared/store/authStore'
import type { UserProfile } from '@/shared/types/module.types'

interface AuthContextValue {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  demoLogin: () => void
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication context provider.
 *
 * Responsibilities:
 * - Attempt silent token refresh on app startup (restores session after page refresh)
 * - Expose login/register/logout actions to the component tree
 * - Provide current user and authentication state
 * - Listen for auth:session-expired events from the Axios interceptor and redirect
 *   to /login via React Router (no full page reload — avoids remount loop)
 *
 * The access token itself lives in the Zustand authStore (so the Axios client can access it).
 * The user profile and isAuthenticated state are derived from the store and reflected here.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const { setAuth, clearAuth } = useAuthStore()
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // ── Silent refresh on mount ──────────────────────────────────
  // On every page load/refresh, attempt to silently restore the session
  // using the HttpOnly refresh token cookie (if it exists).
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await authApi.refresh()
        setAuth(response.accessToken, response.user)
        setUser(response.user)
        setIsAuthenticated(true)
      } catch {
        // No valid refresh token — user needs to log in.
        // Do NOT call navigate here; the ProtectedRoute will handle the redirect
        // once isLoading becomes false and isAuthenticated remains false.
        clearAuth()
        setUser(null)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    restoreSession()
  }, [setAuth, clearAuth])

  // ── Session-expired event from Axios interceptor ─────────────
  // When the interceptor fails to refresh a token mid-session (e.g. cookie
  // expired while the app was open), it dispatches 'auth:session-expired'
  // instead of calling window.location.href — which would cause a full reload
  // and remount this provider, triggering another refresh attempt.
  useEffect(() => {
    const handleSessionExpired = () => {
      clearAuth()
      setUser(null)
      setIsAuthenticated(false)
      navigate('/login', { replace: true })
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [clearAuth, navigate])

  // ── Actions ─────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login({ email, password })
    setAuth(response.accessToken, response.user)
    setUser(response.user)
    setIsAuthenticated(true)
  }, [setAuth])

  const demoLogin = useCallback(() => {
    const demoUser: UserProfile = {
      id: 'demo-learner-1',
      email: 'alex@notimetodelay.com',
      name: 'Alex Rivera',
      role: 'USER',
    }
    setAuth('demo-session-token', demoUser)
    setUser(demoUser)
    setIsAuthenticated(true)
  }, [setAuth])

  const register = useCallback(async (email: string, password: string, name: string) => {
    const response = await authApi.register({ email, password, name })
    setAuth(response.accessToken, response.user)
    setUser(response.user)
    setIsAuthenticated(true)
  }, [setAuth])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      clearAuth()
      setUser(null)
      setIsAuthenticated(false)
    }
  }, [clearAuth])

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, demoLogin, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
